import apiClient from "./api";
import {
  normalizePromotionDiscountType,
  serializePromotionDays,
  type PromotionDiscountType,
  type PromotionRecord,
} from "@/utils/promotion";

interface DiscountPlanApiRecord {
  id?: number;
  code: string;
  name: string;
  status?: number | boolean;
  customer_id?: number | null;
  customer_code?: string | null;
  customer_name?: string | null;
  description?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface DiscountApiRecord {
  id?: number;
  discount_plan_id?: number | null;
  plan_code?: string | null;
  plan_name?: string | null;
  code: string;
  name: string;
  value: number;
  applicable_for?: number | boolean;
  valid_from?: string | null;
  valid_till?: string | null;
  discount_type?: string | null;
  valid_day?: string | null;
  status?: number | boolean;
  created_at?: string;
  updated_at?: string;
}

export interface PromotionPayload {
  code: string;
  name: string;
  customer_id?: number | null;
  customer_code?: string | null;
  customer_name?: string | null;
  value: number;
  discount_type: PromotionDiscountType;
  valid_from?: string | null;
  valid_till?: string | null;
  valid_days?: string[];
  status: number;
  description?: string | null;
}

const normalizeApiList = <T>(payload: any): T[] => {
  const value = payload?.data?.data ?? payload?.data ?? payload;
  return Array.isArray(value) ? value : [];
};

const toPromotionRecord = (
  plan: DiscountPlanApiRecord | undefined,
  discount: DiscountApiRecord
): PromotionRecord => ({
  id: Number(discount.id || 0),
  plan_id: plan?.id ? Number(plan.id) : Number(discount.discount_plan_id || 0) || null,
  plan_code: plan?.code || discount.plan_code || null,
  plan_name: plan?.name || discount.plan_name || null,
  code: discount.code,
  name: discount.name,
  description: plan?.description || "",
  customer_id: plan?.customer_id ? Number(plan.customer_id) : null,
  customer_code: plan?.customer_code || null,
  customer_name: plan?.customer_name || null,
  value: Number(discount.value || 0),
  discount_type: normalizePromotionDiscountType(discount.discount_type),
  valid_from: discount.valid_from || null,
  valid_till: discount.valid_till || null,
  valid_day: discount.valid_day || null,
  status: Number(discount.status ?? 1),
  plan_status: Number(plan?.status ?? 1),
  applicable_for: discount.applicable_for ?? 1,
  created_at: discount.created_at || plan?.created_at,
  updated_at: discount.updated_at || plan?.updated_at,
});

const buildPlanPayload = (payload: PromotionPayload) => ({
  code: `${payload.code}-PLAN`,
  name: payload.name,
  status: Number(payload.status ?? 1),
  customer_id: payload.customer_id || null,
  customer_code: payload.customer_code || null,
  customer_name: payload.customer_name || null,
  description: payload.description || null,
});

const buildDiscountPayload = (
  payload: PromotionPayload,
  plan: DiscountPlanApiRecord
) => ({
  discount_plan_id: plan.id,
  plan_code: plan.code,
  plan_name: plan.name,
  code: payload.code,
  name: payload.name,
  value: Number(payload.value || 0),
  applicable_for: 1,
  valid_from: payload.valid_from || null,
  valid_till: payload.valid_till || null,
  discount_type: normalizePromotionDiscountType(payload.discount_type),
  valid_day: serializePromotionDays(payload.valid_days || []),
  status: Number(payload.status ?? 1),
});

const promotionService = {
  async getPromotions(): Promise<PromotionRecord[]> {
    const [plansResponse, discountsResponse] = await Promise.all([
      apiClient.get("/discount-plans"),
      apiClient.get("/discounts"),
    ]);

    const plans = normalizeApiList<DiscountPlanApiRecord>(plansResponse);
    const discounts = normalizeApiList<DiscountApiRecord>(discountsResponse);
    const plansById = new Map(
      plans
        .filter((plan) => Number(plan.id || 0) > 0)
        .map((plan) => [Number(plan.id), plan] as const)
    );

    return discounts.map((discount) =>
      toPromotionRecord(
        Number(discount.discount_plan_id || 0)
          ? plansById.get(Number(discount.discount_plan_id))
          : undefined,
        discount
      )
    );
  },

  async getAvailablePromotions(): Promise<PromotionRecord[]> {
    const [plansResponse, discountsResponse] = await Promise.all([
      apiClient.get("/discount-plans/available", {
        params: { active_only: true },
      }),
      apiClient.get("/discounts/available", {
        params: { active_only: true },
      }),
    ]);

    const plans = normalizeApiList<DiscountPlanApiRecord>(plansResponse);
    const discounts = normalizeApiList<DiscountApiRecord>(discountsResponse);
    const plansById = new Map(
      plans
        .filter((plan) => Number(plan.id || 0) > 0)
        .map((plan) => [Number(plan.id), plan] as const)
    );

    return discounts.map((discount) =>
      toPromotionRecord(
        Number(discount.discount_plan_id || 0)
          ? plansById.get(Number(discount.discount_plan_id))
          : undefined,
        discount
      )
    );
  },

  async createPromotion(payload: PromotionPayload): Promise<PromotionRecord> {
    const createdPlanResponse = await apiClient.post(
      "/discount-plans",
      buildPlanPayload(payload)
    );
    const createdPlan =
      createdPlanResponse.data?.data ?? createdPlanResponse.data;

    try {
      const createdDiscountResponse = await apiClient.post(
        "/discounts",
        buildDiscountPayload(payload, createdPlan)
      );
      const createdDiscount =
        createdDiscountResponse.data?.data ?? createdDiscountResponse.data;

      return toPromotionRecord(createdPlan, createdDiscount);
    } catch (error) {
      if (createdPlan?.id) {
        try {
          await apiClient.delete(`/discount-plans/${createdPlan.id}`);
        } catch {
          // Keep the original API error for the UI.
        }
      }

      throw error;
    }
  },

  async updatePromotion(
    promotion: PromotionRecord,
    payload: PromotionPayload
  ): Promise<PromotionRecord> {
    let plan: DiscountPlanApiRecord | undefined;

    if (promotion.plan_id) {
      const planResponse = await apiClient.put(
        `/discount-plans/${promotion.plan_id}`,
        buildPlanPayload(payload)
      );
      plan = planResponse.data?.data ?? planResponse.data;
    } else {
      const createdPlanResponse = await apiClient.post(
        "/discount-plans",
        buildPlanPayload(payload)
      );
      plan = createdPlanResponse.data?.data ?? createdPlanResponse.data;
    }

    if (!plan) {
      throw new Error("Unable to prepare the promotion plan for update.");
    }

    const discountResponse = await apiClient.put(
      `/discounts/${promotion.id}`,
      buildDiscountPayload(payload, plan)
    );
    const updatedDiscount = discountResponse.data?.data ?? discountResponse.data;

    return toPromotionRecord(plan, updatedDiscount);
  },

  async deletePromotion(promotion: PromotionRecord): Promise<void> {
    await apiClient.delete(`/discounts/${promotion.id}`);

    if (promotion.plan_id) {
      try {
        await apiClient.delete(`/discount-plans/${promotion.plan_id}`);
      } catch {
        // Ignore cleanup failures so the main delete action still succeeds.
      }
    }
  },
};

export default promotionService;
